import {
    PopoverContent,
    PopoverRoot,
    PopoverTrigger,
} from '@/src/common/components/elements/Buttons/Popover'
import BoxLineItemSummary from '@/src/modules/OrderStatus/content/BoxLineItemSummary'
import {
    buildFeeDescriptions,
    getCustomerFacingOrderStatus,
    getDeliveryStep,
    getLineItemsForOrderStatus,
    getStatusMessage,
} from '@/src/modules/OrderStatus/utilities'
import { faInfo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from '../../common/components/marketing/buttons/Button'
import HeadlineEyebrow from '../../common/components/marketing/headlines/HeadlineEyebrow'
import MulticolorText from '../../common/components/marketing/text/MulticolorText'
import { styled, theme } from '../../stitches.config'
import LineItemSummary from './content/LineItemSummary'
import OrderCancelModal from './content/OrderCancelModal'
import { IOrderStatusDetails } from '@/src/modules/OrderStatus/types'
import { MEDIUM_TYPE_PHYSICAL } from '@/prisma/schemaConstants'
import { DeliveryTracker } from '@/src/common/components/elements/DeliveryTracker/DeliveryTracker'
import ResendGiftCardModal from '@/src/common/components/marketing/modals/ModalResendGiftCard'
import { ResendRedemptionEmailInput } from '@/dto/ResendRedemptionEmailInput'
import { Alert } from '@/src/common/components/elements/Forms/Alert'
import usePostResendInvoiceEmail from '@/src/card-linking/hooks/usePostResendInvoiceEmail'
import ReferAFriendHero from '@/src/common/components/marketing/heros/ReferAFriendHero'
import { REFER_A_FRIEND_PAGE } from '@/src/modules/pg-plus/constants'
import {
    REFER_BODY_TEXT,
    REFER_BUTTON_TEXT,
    REFER_GRADIENT_TEXT,
    REFER_HEADER_TEXT,
    REFER_IMAGE_GROUP,
} from '@/src/common/components/marketing/heros/constants'
import { useState } from 'react'
import useFeatureFlag from '@/src/common/hooks/useFeatureFlag'
import { usePartnerTheme } from '@/src/common/contexts/PartnerThemeContext'

const Container = styled('div', {
    display: 'flex',
    jc: 'center',
    variants: {
        backgroundColor: {
            lightGray: {
                bc: '$lightest',
            },
            white: {
                bc: '$white',
            },
        },
    },
    '.bold': {
        fontWeight: 'bold',
    },
    '.gradient': {
        background: `-webkit-linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 'bold',
    },
    '.capitalize': {
        textTransform: 'capitalize',
    },
})

const TopContentContainer = styled('div', {
    display: 'flex',
    jc: 'space-between',
    p: '30px 0 10px',
    fd: 'column',
    '@md': {
        fd: 'row',
        p: '40px 0',
    },
})

const CenterContent = styled('div', {
    display: 'flex',
    fd: 'column',
    width: '90%',
    maxWidth: '1200px',
    margin: 'auto',
})

const LeftContent = styled('div', {
    display: 'flex',
    fd: 'column',
    jc: 'center',
})

const RightContent = styled('div', {
    display: 'flex',
    fd: 'column',
    jc: 'center',
    mb: '2rem',
    '@md': {
        mb: 0,
    },
    '.status-button': {
        textTransform: 'uppercase',
        fontSize: '18px',
        mt: '10px',
        '@media (min-width: 900px)': {
            mt: 0,
        },
    },
})

const StyledLine = styled('div', {
    maxWidth: '65px',
    maxHeight: 0,
    border: '1px solid $secondary',
})

const Content = styled('div', {
    display: 'flex',
    fd: 'column',
    m: '30px 0 0',
    '@md': {
        m: '35px 0 10px',
        p: '30px 60px',
        boxShadow: '0px 0px 25px rgba(1, 47, 142, 0.4)',
        borderRadius: '25px',
    },
    '.promo': {
        color: 'green',
    },
    '.order-details': {
        pt: 20,
    },
    '.order-status-label': {
        fontWeight: 'bold',
    },
    '.order-summary': {
        background: '$light',
        p: '10px 20px',
        br: '20px',
        mt: '30px',
        mb: '20px',
        '& p': {
            fontSize: '20px',
            m: '10px 0',
            lh: '1.75rem',
        },
        '.two-column': {
            width: '100%',
            '@sm': {
                width: '44%',
            },
        },
        '@sm': {
            display: 'flex',
            jc: 'space-between',
            gap: '40px',
        },
    },
})

const ActionButtonGroup = styled('div', {
    display: 'flex',
    width: '100%',
    marginTop: 30,
    justifyContent: 'center',
    flexDirection: 'row',
    gap: '$2',
    '@lg': {
        flexDirection: 'row',
    },
    '@md': {
        flexDirection: 'column',
        alignItems: 'center',
    },
    '@sm': {
        marginTop: '$8',
    },
})

interface OrderStatusDetailsProps {
    orderStatusDetails: IOrderStatusDetails
    setOrderStatusDetails: Function
}

const OrderStatusDetails = ({
    orderStatusDetails,
    setOrderStatusDetails,
}: OrderStatusDetailsProps) => {
    const {
        order,
        totalGiftLoad,
        virtualRedemptionEmailsSent,
        refund,
        feeGroups,
    } = orderStatusDetails
    const partnerTheme = usePartnerTheme()
    const { totalRefund, isFullyRefunded } = refund
    let {
        status,
        pending_email_verification,
        pending_artwork_review,
        pending_fraud_review,
        kyc_account_review_status,
        uuid,
        shipping,
        total,
        number,
        is_cancellable,
        discount,
        deliveries,
        isComplete,
    } = order
    const isMultipleShipments: boolean =
        deliveries.filter((delivery) =>
            delivery.line_items.find(
                (item) => item.product.mediumType === MEDIUM_TYPE_PHYSICAL
            )
        ).length > 1
    const showSingleDeliveryTracker: boolean =
        !isMultipleShipments && !!deliveries[0]?.line_items[0]?.parsed_imb_data

    const feeDescriptions = buildFeeDescriptions(feeGroups)

    const finalStatus = getCustomerFacingOrderStatus(
        status,
        pending_email_verification,
        pending_fraud_review,
        pending_artwork_review,
        kyc_account_review_status,
        isComplete,
        isFullyRefunded
    )
    const popoverText: string = getStatusMessage(finalStatus)
    const [isResendEGiftModalOpen, setIsResendEGiftModalOpen] = useState(false)
    const [eGiftPayload, setEGiftPayload] = useState(
        {} as ResendRedemptionEmailInput
    )

    // @todo remove when refer is ready for prod
    const { enabled: referIsEnabled } = useFeatureFlag('refer_enabled')

    const emailUpdate = (cardUUID: string, newEmail: string) => {
        deliveries = deliveries.map((delivery) => {
            const newDelivery = { ...delivery }
            if (delivery.card_uuid === cardUUID) {
                delivery.delivery_details.email = newEmail
            }

            return newDelivery
        })
    }

    const { mutate: resendInvoiceEmail, status: resendInvoiceStatus } =
        usePostResendInvoiceEmail()

    return (
        <>
            <Container backgroundColor={'lightGray'}>
                <CenterContent>
                    <TopContentContainer>
                        <LeftContent>
                            <StyledLine />
                            <MulticolorText
                                startText={'Check your'}
                                colorText={'order status'}
                                secondaryColor={{
                                    useGradient: true,
                                    gradient: {
                                        rotation: 63,
                                        // @ts-ignore
                                        firstColor: partnerTheme?.colors?.primary?.value ?? theme.colors.primary.value,
                                        // firstColor: theme.colors.primary.value,
                                        firstColorPercent: 19,
                                        // @ts-ignore
                                        secondColor: partnerTheme?.colors?.secondary?.value ?? theme.colors.secondary.value,
                                        // secondColor: theme.colors.secondary.value,
                                        secondColorPercent: 82,
                                    },
                                }}
                                htmlElement={'h1'}
                                additionalStyles={{
                                    fontSize: theme.fontSizes['8'],
                                    paddingRight: '40px',
                                    margin: '1rem 0',
                                    '@md': {
                                        fontSize: theme.fontSizes['9'],
                                    },
                                }}
                            />
                        </LeftContent>
                        <RightContent>
                            <Button
                                className={'status-button'}
                                variant={'primary'}
                                size={'md'}
                                showArrow
                                label={'Check another order'}
                                onClick={() => {
                                    setOrderStatusDetails(null)
                                }}
                            />
                        </RightContent>
                    </TopContentContainer>
                </CenterContent>
            </Container>
            <Container backgroundColor={'white'}>
                <CenterContent>
                    <Content>
                        <div className={'order-details'}>
                            <HeadlineEyebrow
                                label={`Details for order #${number}`}
                            />
                            <div className={'order-summary'}>
                                <div
                                    className={totalRefund ? 'two-column' : ''}
                                >
                                    <p>
                                        <span className={'order-status-label'}>
                                            Order status
                                        </span>
                                        :{' '}
                                        <span className={'gradient capitalize'}>
                                            {finalStatus}
                                        </span>
                                        &nbsp;
                                        {popoverText && (
                                            <PopoverRoot>
                                                <PopoverTrigger
                                                    variant={'purpleAlt'}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faInfo}
                                                    />
                                                </PopoverTrigger>
                                                <PopoverContent>
                                                    {popoverText}
                                                </PopoverContent>
                                            </PopoverRoot>
                                        )}
                                    </p>
                                    <p>
                                        Gift card value: $
                                        {totalGiftLoad.toFixed(2)}
                                    </p>
                                    {feeDescriptions}
                                    {shipping > 0 && (
                                        <p>Shipping: ${shipping}</p>
                                    )}
                                    {discount > 0 && (
                                        <p>
                                            Promo Discount:{' '}
                                            <span className={'promo'}>
                                                -${discount}
                                            </span>
                                        </p>
                                    )}
                                    <p className={'bold'}>
                                        Order total: ${total.toFixed(2)}
                                    </p>
                                </div>
                                {totalRefund > 0 && (
                                    <div className={'two-column'}>
                                        <p className={'bold'}>
                                            Refunded amount: ${totalRefund}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {showSingleDeliveryTracker && (
                                <DeliveryTracker
                                    step={getDeliveryStep({
                                        orderStatus: status,
                                        lineItemStatus:
                                            deliveries[0].line_items[0].status,
                                    })}
                                    imbData={
                                        deliveries[0]?.line_items[0]
                                            ?.parsed_imb_data
                                    }
                                    style={{
                                        width: '100%',
                                        padding: '16px 0px',
                                    }}
                                />
                            )}
                        </div>
                        <>
                            {deliveries.map((delivery) => {
                                const lineItems = delivery.line_items
                                const { lineItemsInBoxes, lineItemsNotInBox } =
                                    getLineItemsForOrderStatus(lineItems)

                                return (
                                    <div key={`delivery-${delivery.uuid}`}>
                                        {lineItemsInBoxes?.map(
                                            (lineItem, index) => {
                                                return (
                                                    <BoxLineItemSummary
                                                        key={`line-item-${index}`}
                                                        lineItem={
                                                            lineItem.items[0]
                                                        }
                                                        orderStatus={status}
                                                        virtualRedemptionEmailsSent={
                                                            virtualRedemptionEmailsSent
                                                        }
                                                        quantity={
                                                            lineItem.items
                                                                .length
                                                        }
                                                        deliveryDetails={
                                                            delivery.delivery_details
                                                        }
                                                        isMultipleShipments={
                                                            isMultipleShipments
                                                        }
                                                    />
                                                )
                                            }
                                        )}
                                        {lineItemsNotInBox?.map((lineItem) => {
                                            return (
                                                <LineItemSummary
                                                    key={`line-item-${lineItem.id}`}
                                                    orderStatus={status}
                                                    cardUUID={
                                                        delivery.card_uuid
                                                    }
                                                    lineItem={lineItem}
                                                    virtualRedemptionEmailsSent={
                                                        virtualRedemptionEmailsSent
                                                    }
                                                    deliveryDetails={
                                                        delivery.delivery_details
                                                    }
                                                    isMultipleShipments={
                                                        isMultipleShipments
                                                    }
                                                    onClickResentEGift={() => {
                                                        setEGiftPayload({
                                                            order_number:
                                                                order.number,
                                                            email_address:
                                                                delivery
                                                                    .delivery_details
                                                                    .email!,
                                                            medium_type:
                                                                lineItem.product
                                                                    .mediumType,
                                                            card_uuid:
                                                                delivery.card_uuid ??
                                                                '',
                                                        })
                                                        setIsResendEGiftModalOpen(
                                                            true
                                                        )
                                                    }}
                                                />
                                            )
                                        })}
                                    </div>
                                )
                            })}
                        </>

                        <ActionButtonGroup>
                            {is_cancellable && (
                                <OrderCancelModal orderUuid={uuid} />
                            )}
                            <Button
                                className={'status-button'}
                                variant={'primary'}
                                size={'md'}
                                label={'Resend Invoice'}
                                onClick={() =>
                                    resendInvoiceEmail({
                                        order_number: number,
                                    })
                                }
                            />
                        </ActionButtonGroup>
                        {(resendInvoiceStatus === 'error' ||
                            resendInvoiceStatus === 'success') && (
                            <Alert
                                variant={
                                    resendInvoiceStatus === 'success'
                                        ? 'success'
                                        : 'error'
                                }
                                label={
                                    resendInvoiceStatus === 'success'
                                        ? 'An invoice was sent to the email used to place the order.'
                                        : 'An error occurred while attempting to resend your invoice to your email.'
                                }
                                css={{ marginTop: '10px' }}
                            />
                        )}
                    </Content>
                </CenterContent>
                <ResendGiftCardModal
                    eGiftPayload={eGiftPayload}
                    showModal={isResendEGiftModalOpen}
                    setShowModal={setIsResendEGiftModalOpen}
                    emailUpdateCallback={emailUpdate}
                />
            </Container>
            {referIsEnabled && (
                <ReferAFriendHero
                    headerText={REFER_HEADER_TEXT}
                    gradientText={REFER_GRADIENT_TEXT}
                    bodyText={REFER_BODY_TEXT}
                    buttonLabel={REFER_BUTTON_TEXT}
                    buttonUrl={REFER_A_FRIEND_PAGE}
                    imageUrl={REFER_IMAGE_GROUP}
                    variant={'image-left'}
                    style={{ marginTop: 75, marginBottom: 30, maxWidth: '90%' }}
                />
            )}
        </>
    )
}

export default OrderStatusDetails
